import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { addMember, setCurrentMember, setSearchQuery as setMemberSearchQuery } from '../store/slices/membersSlice';
import { motion, AnimatePresence } from 'framer-motion';
import { UserX, Search, Mail, Phone, MoreVertical, Shield, Plus, X, User, GraduationCap, Book, Clock, Calendar, CheckCircle, Users, Upload, ChevronDown, Printer, FileText, Settings } from 'lucide-react';
import createUser from '../api/CreateUser.js';
import UpdateUser from '../api/UpdateUser.js';
import { cn } from '../lib/utils';
import Papa from 'papaparse';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import MemberDetailsModal from '../components/MemberDetailsModal';
import PrintQRModal from '../components/PrintQRModal';
import CleanupMembersModal from '../components/CleanupMembersModal';
import ExportCSVModal from '../components/ExportCSVModal';
import ImportCSVModal from '../components/ImportCSVModal';
import ManageClassModal from '../components/ManageClassModal';
import getLoansList from '../api/GetLoansList';
import getUsers from '../api/GetUsers';
import { setMembers } from '../store/slices/membersSlice';

const MemberCard = ({ ID, id, name, role, email, tel, classe, avatar, status, qrcode_data, onClick, onPrintQR, delay, loanCount }) => {
    const memberId = ID || id;

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ delay, duration: 0.3 }}
            onClick={() => onClick({ id: memberId, ID: memberId, name, role, email, tel, avatar, status })}
            className="bg-card backdrop-blur-xl border border-border rounded-2xl p-6 flex items-center gap-4 group hover:bg-muted/50 transition-colors cursor-pointer relative"
        >
            <div className="absolute top-4 right-4 flex gap-2">
                <button
                    onClick={(e) => { e.stopPropagation(); onPrintQR({ name, ID: memberId, id: memberId, qrcode_data, role, classe }); }}
                    className="p-2 bg-muted rounded-full text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity hover:bg-primary hover:text-white"
                    title="Imprimer QR Code"
                >
                    <Printer size={16} />
                </button>
                <button className="p-2 text-muted-foreground hover:text-foreground transition-colors">
                    <MoreVertical size={16} />
                </button>
            </div>

            <div className="relative">
                <img src={avatar} alt={name} className="w-16 h-16 rounded-full object-cover border-2 border-border group-hover:border-primary/50 transition-colors" />
                <div className={`absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-background ${status === 'Actif' ? 'bg-emerald-500' : 'bg-muted-foreground'}`} />
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                    <h3 className="text-foreground font-bold truncate pr-8">{name}</h3>
                </div>
                <div className="flex items-center gap-2 text-xs text-primary mb-2">
                    <Shield size={12} />
                    <span>{role}</span>
                </div>
                <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground truncate">
                        <Mail size={14} />
                        <span>{email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground truncate">
                        <Phone size={14} />
                        <span>{tel}</span>
                    </div>
                    {classe && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground truncate">
                            <GraduationCap size={14} />
                            <span>{classe}</span>
                        </div>
                    )}
                    <div className="flex items-center gap-2 text-sm font-semibold text-primary mt-2">
                        <Book size={14} />
                        <span>{loanCount || 0} emprunt{loanCount > 1 ? 's' : ''}</span>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

const AddMemberModal = ({ isOpen, onClose, onAdd, category, onRefresh }) => {
    const dispatch = useDispatch();
    const { roleAvatars } = useSelector((state) => state.members);
    const API_BASE = useSelector((state) => state.user.apiBase);
    const token = useSelector((state) => state.user.currentUser.token);
    const currentUser = useSelector((state) => state.user.currentUser);
    const code_etablissement = useSelector((state) => state.user.currentUser.code_etablissement);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        tel: '',
        classe: '',
        role: "Étudiant",
        password: ''
    });

    useEffect(() => {
        if (category != "all") {
            setFormData({ ...formData, role: category });
        }
        else {
            setFormData({ ...formData, role: "Étudiant" });
        }
    }, [category]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await createUser(API_BASE, token, {
                "name": formData.name,
                "email": formData.email,
                "tel": formData.tel,
                "password": formData.password || "pardefault",
                "role": formData.role,
                "code_etablissement": code_etablissement,
                "classe": formData.classe
            });
            if (onRefresh) await onRefresh();
            onClose();
            setFormData({ name: '', email: '', tel: '', role: "Étudiant", classe: '', password: '' });
        } catch (err) {
            console.error(err);
            alert("Erreur lors de la création de l'utilisateur : " + err.message);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-background border border-border rounded-2xl p-6 w-full max-w-md shadow-2xl"
            >
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-foreground">Ajouter un membre</h3>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-muted-foreground mb-1">Nom complet</label>
                        <input
                            required
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full bg-muted border border-border rounded-xl py-2 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-muted-foreground mb-1">Email</label>
                        <input
                            required
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="w-full bg-muted border border-border rounded-xl py-2 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-muted-foreground mb-1">Téléphone</label>
                        <input
                            type="tel"
                            value={formData.tel}
                            onChange={(e) => setFormData({ ...formData, tel: e.target.value })}
                            className="w-full bg-muted border border-border rounded-xl py-2 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-muted-foreground mb-1">Rôle</label>
                        <select
                            value={formData.role}
                            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                            className="w-full bg-muted border border-border rounded-xl py-2 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                        >
                            {currentUser.role == 'Bibliothécaire' && <option value="Bibliothécaire">Bibliothécaire</option>}
                            {currentUser.role == 'Bibliothécaire' && <option value="Enseignant">Enseignant</option>}
                            <option value="Étudiant">Étudiant</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-muted-foreground mb-1">Classe</label>
                        <input
                            type="text"
                            value={formData.classe}
                            onChange={(e) => setFormData({ ...formData, classe: e.target.value })}
                            placeholder="Ex: 3ème B"
                            className="w-full bg-muted border border-border rounded-xl py-2 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-muted-foreground mb-1">Mot de passe provisoire</label>
                        <input
                            type="text"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            placeholder="pardefault"
                            className="w-full bg-muted border border-border rounded-xl py-2 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                        />
                    </div>

                    <button
                        type="submit"
                        className="w-full bg-primary text-primary-foreground font-bold py-3 rounded-xl hover:bg-primary/90 transition-colors mt-4"
                    >
                        Créer le membre
                    </button>
                </form>
            </motion.div>
        </div>
    );
};

export default function Members() {
    const dispatch = useDispatch();
    const { items: members, roleAvatars, categories } = useSelector((state) => state.members);
    const currentUser = useSelector((state) => state.user.currentUser);
    const API_BASE = useSelector((state) => state.user.apiBase);
    const token = useSelector((state) => state.user.currentUser?.token);
    const [activeCategory, setActiveCategory] = useState('all');
    const [selectedClass, setSelectedClass] = useState('all');
    const [isClassDropdownOpen, setIsClassDropdownOpen] = useState(false);
    const fileInputRef = useRef(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedMemberId, setSelectedMemberId] = useState(null);
    const [printQRMember, setPrintQRMember] = useState(null);
    const { searchQuery } = useSelector((state) => state.members);
    const [loanCounts, setLoanCounts] = useState({});
    const [sortBy, setSortBy] = useState('name'); // 'name' or 'loans'
    const [isCleanupModalOpen, setIsCleanupModalOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [isManageClassModalOpen, setIsManageClassModalOpen] = useState(false);

    // États pour la modale d'importation CSV
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [importStatus, setImportStatus] = useState('idle'); // 'reading' | 'importing' | 'completed' | 'error'
    const [importProgress, setImportProgress] = useState({ current: 0, total: 0, currentName: '' });
    const [importSummary, setImportSummary] = useState({ created: 0, skipped: 0, errors: 0, errorMessage: '' });

    const refreshMembers = async () => {
        if (!API_BASE || !token || !currentUser?.code_etablissement) return;
        try {
            const result = await getUsers(API_BASE, token, currentUser.code_etablissement, roleAvatars);
            dispatch(setMembers(result.users));
        } catch (error) {
            console.error("Failed to refresh members:", error);
        }
    };

    useEffect(() => {
        const fetchLoanCounts = async () => {
            if (!API_BASE || !token || !currentUser?.code_etablissement) return;
            try {
                const loans = await getLoansList(API_BASE, token, currentUser.code_etablissement);
                const counts = {};
                loans.forEach(loan => {
                    const uid = loan.user_id;
                    counts[uid] = (counts[uid] || 0) + 1;
                });
                setLoanCounts(counts);
            } catch (error) {
                console.error("Failed to fetch loan counts:", error);
            }
        };
        fetchLoanCounts();
    }, [API_BASE, token, currentUser]);

    // Reset class filter when category changes
    useEffect(() => {
        setSelectedClass('all');
    }, [activeCategory]);

    // Base members allowed for this user
    const allowedMembers = members.filter(m => {
        if (!currentUser) return false;
        if (currentUser.role === 'Bibliothécaire') return true;
        if (currentUser.role === 'Enseignant') {
            // Teacher sees students + self
            return m.role === 'Étudiant' || (m.ID || m.id) == (currentUser.id || currentUser.ID);
        }
        if (currentUser.role === 'Étudiant') {
            // Student sees only self
            return (m.ID || m.id) == (currentUser.id || currentUser.ID);
        }
        return false;
    });

    // Extract unique classes for students from allowed members
    const availableClasses = Array.from(new Set(
        allowedMembers
            .filter(m => m.role === 'Étudiant' && m.classe)
            .map(m => m.classe)
    )).sort();

    // Identify old members (> 300 days)
    const oldMembers = members.filter(m => {
        if (!m.UpdatedAt) return false;
        const diffTime = Math.abs(new Date() - new Date(m.UpdatedAt));
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 300;
    });

    const ICON_MAP = {
        'Book': Book,
        'GraduationCap': GraduationCap,
        'User': User,
        'Users': Users
    };

    const tabs = [
        { id: 'all', label: 'Tous les membres', iconName: 'Users' },
        ...categories
    ].filter(tab => {
        // Only show tabs that have members for the current user
        if (tab.id === 'all') return true;
        return allowedMembers.some(m => m.role === tab.id);
    });

    let filteredMembers = activeCategory === 'all'
        ? allowedMembers
        : allowedMembers.filter(m => m.role === activeCategory);

    // Apply class filter if category is Student
    if (activeCategory === 'Étudiant' && selectedClass !== 'all') {
        filteredMembers = filteredMembers.filter(m => m.classe === selectedClass);
    }

    const handleAddMember = (newMember) => {
        dispatch(addMember(newMember));
    };

    const fixMojibake = (str) => {
        if (!str) return '';
        let s = String(str).trim();

        // Si le texte contient des caractères idéogrammes CJK aberrants (ex: 硩), cela indique un décalage d'octets d'Excel
        if (/[\u4E00-\u9FFF]/.test(s)) {
            let replaced = '';
            for (let char of s) {
                const code = char.charCodeAt(0);
                if (code >= 0x4E00 && code <= 0x9FFF) {
                    const b1 = code & 0xFF;
                    const b2 = (code >> 8) & 0xFF;
                    const dec = new TextDecoder('windows-1252');
                    replaced += dec.decode(new Uint8Array([b1, b2])).replace(/\0/g, '');
                } else {
                    replaced += char;
                }
            }
            s = replaced;
        }

        if (/[\u00C2\u00C3]/.test(s)) {
            try {
                s = decodeURIComponent(escape(s));
            } catch (e) {
                s = s
                    .replace(/Ã©/g, 'é').replace(/Ã¨/g, 'è').replace(/Ãª/g, 'ê').replace(/Ã«/g, 'ë')
                    .replace(/Ã/g, 'à').replace(/Ã¢/g, 'â')
                    .replace(/Ã§/g, 'ç')
                    .replace(/Ã®/g, 'î').replace(/Ã¯/g, 'ï')
                    .replace(/Ã´/g, 'ô')
                    .replace(/Ã¹/g, 'ù').replace(/Ã»/g, 'û').replace(/Ã¼/g, 'ü')
                    .replace(/Ã‰/g, 'É').replace(/ÃÈ/g, 'È').replace(/ÃÀ/g, 'À').replace(/ÃÇ/g, 'Ç');
            }
        }
        return s;
    };

    const decodeCSVBuffer = (buffer) => {
        const bytes = new Uint8Array(buffer);

        // 1. Détection des BOMs (Byte Order Mark)
        if (bytes.length >= 2 && bytes[0] === 0xFF && bytes[1] === 0xFE) {
            return new TextDecoder('utf-16le').decode(buffer);
        }
        if (bytes.length >= 2 && bytes[0] === 0xFE && bytes[1] === 0xFF) {
            return new TextDecoder('utf-16be').decode(buffer);
        }
        if (bytes.length >= 3 && bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
            return new TextDecoder('utf-8').decode(buffer);
        }

        // 2. Détection heuristique de UTF-16LE / UTF-16BE sans BOM (fichiers Unicode d'Excel)
        if (bytes.length >= 10) {
            let nullCountOdd = 0;
            let nullCountEven = 0;
            const sampleSize = Math.min(bytes.length, 200);
            for (let i = 0; i < sampleSize; i++) {
                if (bytes[i] === 0) {
                    if (i % 2 === 1) nullCountOdd++;
                    else nullCountEven++;
                }
            }
            if (nullCountOdd > sampleSize / 4) {
                return new TextDecoder('utf-16le').decode(buffer);
            }
            if (nullCountEven > sampleSize / 4) {
                return new TextDecoder('utf-16be').decode(buffer);
            }
        }

        // 3. Décodage hybride octet par octet (Combine les séquences UTF-8 valides ET les octets isolés Windows-1252 comme \xE7 = ç)
        const winDecoder = new TextDecoder('windows-1252');
        const utf8Decoder = new TextDecoder('utf-8');
        let result = '';
        let i = 0;

        while (i < bytes.length) {
            const b1 = bytes[i];

            // ASCII 1 octet (0x00 - 0x7F)
            if (b1 < 0x80) {
                result += String.fromCharCode(b1);
                i++;
                continue;
            }

            // Séquence UTF-8 à 2 octets (0xC2 - 0xDF)
            if (b1 >= 0xC2 && b1 <= 0xDF && i + 1 < bytes.length) {
                const b2 = bytes[i + 1];
                if (b2 >= 0x80 && b2 <= 0xBF) {
                    result += utf8Decoder.decode(bytes.subarray(i, i + 2));
                    i += 2;
                    continue;
                }
            }

            // Séquence UTF-8 à 3 octets (0xE0 - 0xEF)
            if (b1 >= 0xE0 && b1 <= 0xEF && i + 2 < bytes.length) {
                const b2 = bytes[i + 1];
                const b3 = bytes[i + 2];
                if (b2 >= 0x80 && b2 <= 0xBF && b3 >= 0x80 && b3 <= 0xBF) {
                    result += utf8Decoder.decode(bytes.subarray(i, i + 3));
                    i += 3;
                    continue;
                }
            }

            // Séquence UTF-8 à 4 octets (0xF0 - 0xF4)
            if (b1 >= 0xF0 && b1 <= 0xF4 && i + 3 < bytes.length) {
                const b2 = bytes[i + 1];
                const b3 = bytes[i + 2];
                const b4 = bytes[i + 3];
                if (b2 >= 0x80 && b2 <= 0xBF && b3 >= 0x80 && b3 <= 0xBF && b4 >= 0x80 && b4 <= 0xBF) {
                    result += utf8Decoder.decode(bytes.subarray(i, i + 4));
                    i += 4;
                    continue;
                }
            }

            // Octet isolé Windows-1252 (ex: \xE7 = ç inséré par Excel dans un fichier UTF-8)
            result += winDecoder.decode(bytes.subarray(i, i + 1));
            i++;
        }

        return result;
    };

    const handleCSVImport = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsImportModalOpen(true);
        setImportStatus('reading');
        setImportProgress({ current: 0, total: 0, currentName: '' });
        setImportSummary({ created: 0, updated: 0, errors: 0, errorMessage: '' });

        try {
            const buffer = await file.arrayBuffer();
            let text = decodeCSVBuffer(buffer);

            // Supprimer le BOM UTF-8 s'il est présent
            text = text.replace(/^\uFEFF/, '');

            Papa.parse(text, {
                header: true,
                skipEmptyLines: true,
                transformHeader: (header) => fixMojibake(header.trim().replace(/^["']|["']$/g, '')),
                complete: async (results) => {
                    const { data } = results;
                    const total = data.length;

                    setImportStatus('importing');
                    setImportProgress({ current: 0, total, currentName: '' });

                    let createdCount = 0;
                    let updatedCount = 0;
                    let errorCount = 0;

                    for (let i = 0; i < data.length; i++) {
                        const row = data[i];

                        // Extraction avec correction des caractères accentués
                        const rawNom = row.nom || row.Nom || row.name || row.Name || "";
                        const rawPrenom = row.prénom || row.prenom || row.Prénom || row.Prenom || "";
                        const rawEmail = row.email || row.Email || "";
                        const rawRole = row.role || row.Role || row.Rôle || row.rôle || "Étudiant";
                        const rawTel = row.tel || row.Tel || row.téléphone || row.Téléphone || "";
                        const rawClasse = row.classe || row.Classe || "";

                        const nom = fixMojibake(rawNom);
                        const prenom = fixMojibake(rawPrenom);
                        const email = fixMojibake(rawEmail).toLowerCase();
                        const role = fixMojibake(rawRole) || "Étudiant";
                        const tel = fixMojibake(rawTel);
                        const classe = fixMojibake(rawClasse);

                        const name = prenom ? `${nom} ${prenom}`.trim() : nom.trim();

                        setImportProgress({
                            current: i + 1,
                            total,
                            currentName: name || email || `Ligne ${i + 1}`
                        });

                        if (!name || !email) {
                            errorCount++;
                            setImportSummary(s => ({ ...s, errors: errorCount }));
                            continue;
                        }

                        // Rechercher si le membre existe déjà dans la liste (par email)
                        const existingMember = members.find(m =>
                            m.email && m.email.toLowerCase() === email
                        );

                        const userData = {
                            name,
                            email,
                            tel: tel || "",
                            role: role || "Étudiant",
                            code_etablissement: currentUser.code_etablissement,
                            classe: classe || ""
                        };

                        if (existingMember) {
                            // Le membre existe déjà -> Mise à jour
                            try {
                                const memberId = existingMember.ID || existingMember.id;
                                await UpdateUser(memberId, userData);
                                updatedCount++;
                                setImportSummary(s => ({ ...s, updated: updatedCount }));
                            } catch (err) {
                                errorCount++;
                                setImportSummary(s => ({ ...s, errors: errorCount }));
                            }
                        } else {
                            // Le membre n'existe pas -> Création
                            try {
                                await createUser(API_BASE, token, {
                                    ...userData,
                                    password: "pardefault"
                                });
                                createdCount++;
                                setImportSummary(s => ({ ...s, created: createdCount }));
                            } catch (err) {
                                errorCount++;
                                setImportSummary(s => ({ ...s, errors: errorCount }));
                            }
                        }
                    }

                    // Rafraîchissement serveur officiel pour synchroniser tous les membres
                    await refreshMembers();

                    setImportStatus('completed');
                    e.target.value = null;
                },
                error: (err) => {
                    setImportStatus('error');
                    setImportSummary(s => ({ ...s, errorMessage: err?.message || "Erreur lors de l'analyse du CSV." }));
                    e.target.value = null;
                }
            });
        } catch (err) {
            console.error("Erreur de lecture du fichier CSV :", err);
            setImportStatus('error');
            setImportSummary(s => ({ ...s, errorMessage: "Impossible de lire le fichier." }));
            e.target.value = null;
        }
    };
    const handleCSVExport = (onlyStudents = false) => {
        let listToExport = members;
        if (onlyStudents) {
            listToExport = members.filter(m => m.role === 'Étudiant');
        }

        // trier par role
        const sortedMembers = [...listToExport].sort((a, b) => (a.role || "").localeCompare(b.role || ""));

        const exportData = sortedMembers.map(m => {
            const nameParts = (m.name || "").trim().split(/\s+/);
            const nom = nameParts[0] || "";
            const prenom = nameParts.slice(1).join(' ') || "";

            return {
                nom: nom,
                prénom: prenom,
                email: m.email || "",
                role: m.role || "",
                tel: m.tel || "",
                classe: m.classe || ""
            };
        });

        const csv = Papa.unparse(exportData);
        // Ajout du BOM UTF-8 '\uFEFF' pour forcer Excel à ouvrir le fichier en UTF-8 natif sous Windows
        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `membres_${onlyStudents ? 'etudiants_' : ''}${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleExportQRCodesPDF = () => {
        // Only include students with QR codes
        const studentsWithQR = allowedMembers.filter(m => m.role === 'Étudiant' && m.qrcode_data);

        if (studentsWithQR.length === 0) {
            alert("Aucun étudiant avec QR code trouvé.");
            return;
        }

        const doc = new jsPDF();

        // Group by class
        const classMap = new Map();

        studentsWithQR.forEach(student => {
            const className = student.classe || 'Sans classe';
            if (!classMap.has(className)) {
                classMap.set(className, []);
            }
            classMap.get(className).push(student);
        });

        // Sort classes
        const sortedClasses = Array.from(classMap.keys()).sort();

        // PDF Grid configuration
        const cols = 3;
        const rows = 4;
        const marginX = 15;
        const marginY = 30;
        const cellWidth = (210 - 2 * marginX) / cols;
        const cellHeight = (297 - marginY - 20) / rows; // A4 height is 297mm
        const qrSize = Math.min(cellWidth - 10, cellHeight - 20); // padding

        sortedClasses.forEach((className, classIndex) => {
            if (classIndex > 0) {
                doc.addPage();
            }

            const studentsList = classMap.get(className).sort((a, b) => a.name.localeCompare(b.name));

            // Title
            doc.setFontSize(18);
            doc.setTextColor(40, 40, 40);
            doc.setFont(undefined, 'bold');
            doc.text(`QR Codes - Classe : ${className}`, 210 / 2, 20, { align: 'center' });

            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(100, 100, 100);
            doc.text(`Généré le ${new Date().toLocaleDateString()} - ${studentsList.length} élève(s)`, 210 / 2, 26, { align: 'center' });

            let countOnPage = 0;

            studentsList.forEach((student, index) => {
                if (countOnPage >= cols * rows) {
                    doc.addPage();
                    countOnPage = 0;

                    doc.setFontSize(18);
                    doc.setTextColor(40, 40, 40);
                    doc.setFont(undefined, 'bold');
                    doc.text(`QR Codes - Classe : ${className} (Suite)`, 210 / 2, 20, { align: 'center' });
                }

                // Calculate position
                const colIndex = countOnPage % cols;
                const rowIndex = Math.floor(countOnPage / cols);

                const x = marginX + (colIndex * cellWidth);
                const y = marginY + (rowIndex * cellHeight);

                // Draw cell border
                doc.setDrawColor(200, 200, 200);
                doc.rect(x + 2, y + 2, cellWidth - 4, cellHeight - 4);

                // Add QR Code Image
                const imgData = "data:image/png;base64," + student.qrcode_data;
                const qrX = x + (cellWidth - qrSize) / 2;
                const qrY = y + 5;
                doc.addImage(imgData, 'PNG', qrX, qrY, qrSize, qrSize);

                // Add Name
                doc.setFontSize(10);
                doc.setTextColor(0);
                doc.setFont(undefined, 'bold');

                // Truncate name if too long
                const splitName = doc.splitTextToSize(student.name, cellWidth - 6);
                const nameY = qrY + qrSize + 6;
                doc.text(splitName[0], x + cellWidth / 2, nameY, { align: 'center' });

                // Add ID
                doc.setFontSize(8);
                doc.setFont(undefined, 'normal');
                doc.setTextColor(100);
                doc.text(`ID: ${student.ID || student.id}`, x + cellWidth / 2, nameY + 5, { align: 'center' });

                countOnPage++;
            });
        });

        doc.save(`qrcodes_eleves_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    useEffect(() => {
        //console.log("Members:", members);
    }, [members]);

    const { currentMember } = useSelector((state) => state.members);

    useEffect(() => {
        if (currentMember) {
            setSelectedMemberId(currentMember.ID);
            dispatch(setCurrentMember(null));
        }
    }, [currentMember, dispatch]);
    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-3xl font-bold text-foreground">Membres</h2>
                    <p className="text-muted-foreground mt-1">Gérer les membres et les permissions</p>
                </div>

                {currentUser?.role !== 'Étudiant' && (
                    <div className="flex items-center gap-3">
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleCSVImport}
                            accept=".csv"
                            className="hidden"
                        />
                        {currentUser?.role === 'Bibliothécaire' && (
                            <button
                                onClick={handleExportQRCodesPDF}
                                className="flex items-center gap-2 bg-slate-800 text-white px-4 py-2 rounded-xl font-medium hover:bg-slate-700 transition-colors border border-border"
                                title="Exporter les QR Codes des élèves (PDF)"
                            >
                                <FileText size={18} />
                                <span className="hidden sm:inline">QR Codes</span>
                            </button>
                        )}
                        {currentUser?.role !== 'Enseignant' && (
                            <>
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="flex items-center gap-2 bg-muted text-foreground px-4 py-2 rounded-xl font-medium hover:bg-muted/80 transition-colors border border-border"
                                >
                                    <Upload size={18} />
                                    <span className="hidden sm:inline">Importer CSV</span>
                                </button>
                                <button
                                    onClick={() => setIsExportModalOpen(true)}
                                    className="flex items-center gap-2 bg-muted text-foreground px-4 py-2 rounded-xl font-medium hover:bg-muted/80 transition-colors border border-border"
                                    title="Exporter les membres (CSV)"
                                >
                                    <FileText size={18} />
                                    <span className="hidden sm:inline">Exporter CSV</span>
                                </button>
                            </>
                        )}
                        <button
                            onClick={() => {
                                setIsModalOpen(true)

                            }}
                            className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-xl font-medium hover:bg-primary/90 transition-colors"
                        >
                            <Plus size={18} />
                            <span>Ajouter</span>
                        </button>

                        {currentUser?.role === 'Bibliothécaire' && (
                            <button
                                onClick={() => setIsCleanupModalOpen(true)}
                                className="flex items-center gap-2 bg-red-500/10 text-red-500 px-4 py-2 rounded-xl font-medium hover:bg-red-500/20 transition-colors border border-red-500/20"
                                title="Voir les membres inactifs depuis > 300 jours"
                            >
                                <UserX size={18} />
                                <span>Effacer les anciens membres</span>
                            </button>
                        )}
                    </div>
                )}
            </div>

            {currentUser?.role !== 'Étudiant' && (
                <div className="flex flex-wrap items-center gap-4">
                    {/* Categories Tabs */}
                    <div className="flex p-1 bg-muted/50 backdrop-blur-xl border border-border rounded-xl w-fit">
                        {tabs.map((tab) => {
                            const IconComponent = ICON_MAP[tab.iconName] || Users;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveCategory(tab.id)}
                                    className={cn(
                                        "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all",
                                        activeCategory === tab.id
                                            ? "bg-primary text-primary-foreground shadow-lg"
                                            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                                    )}
                                >
                                    <IconComponent size={16} />
                                    <span>{tab.id != "all" ? tab.label + " (" + allowedMembers.filter((t) => t.role === tab.id).length + ")" : tab.label + " (" + allowedMembers.length + ")"}</span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Class Selector for Students (Custom Dropdown) */}
                    <AnimatePresence>
                        {activeCategory === 'Étudiant' && availableClasses.length > 0 && (
                            <div className="flex items-center gap-2">
                                <div className="relative">
                                    <motion.button
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        onClick={() => setIsClassDropdownOpen(!isClassDropdownOpen)}
                                        className="flex items-center gap-2 bg-muted/50 backdrop-blur-xl border border-border rounded-xl px-4 py-2 hover:bg-muted/80 transition-all text-sm font-medium text-foreground min-w-[160px] justify-between"
                                    >
                                        <div className="flex items-center gap-2">
                                            <GraduationCap size={16} className="text-primary" />
                                            <span>{selectedClass === 'all' ? 'Toutes les classes' : selectedClass}</span>
                                        </div>
                                        <motion.div
                                            animate={{ rotate: isClassDropdownOpen ? 180 : 0 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            <ChevronDown size={14} className="text-muted-foreground" />
                                        </motion.div>
                                    </motion.button>

                                    <AnimatePresence>
                                        {isClassDropdownOpen && (
                                            <>
                                                {/* Backdrop to close on click outside */}
                                                <div
                                                    className="fixed inset-0 z-10"
                                                    onClick={() => setIsClassDropdownOpen(false)}
                                                />

                                                <motion.div
                                                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                                    className="absolute top-full left-0 mt-2 w-full min-w-[200px] bg-card/95 backdrop-blur-2xl border border-border rounded-xl shadow-2xl overflow-hidden z-20"
                                                >
                                                    <div className="p-1">
                                                        <button
                                                            onClick={() => {
                                                                setSelectedClass('all');
                                                                setIsClassDropdownOpen(false);
                                                            }}
                                                            className={cn(
                                                                "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
                                                                selectedClass === 'all'
                                                                    ? "bg-primary text-primary-foreground font-bold"
                                                                    : "text-foreground hover:bg-muted/50"
                                                            )}
                                                        >
                                                            Toutes les classes
                                                        </button>
                                                        {availableClasses.map(classe => (
                                                            <button
                                                                key={classe}
                                                                onClick={() => {
                                                                    setSelectedClass(classe);
                                                                    setIsClassDropdownOpen(false);
                                                                }}
                                                                className={cn(
                                                                    "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
                                                                    selectedClass === classe
                                                                        ? "bg-primary text-primary-foreground font-bold"
                                                                        : "text-foreground hover:bg-muted/50"
                                                                )}
                                                            >
                                                                {classe}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </motion.div>
                                            </>
                                        )}
                                    </AnimatePresence>
                                </div>

                                {selectedClass !== 'all' && (
                                    <motion.button
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.8 }}
                                        onClick={() => setIsManageClassModalOpen(true)}
                                        className="p-2.5 bg-muted/50 backdrop-blur-xl border border-border rounded-xl hover:bg-muted/80 text-foreground transition-all shadow-sm flex items-center justify-center"
                                        title={`Gérer la classe ${selectedClass} (Passage de niveau, sortie...)`}
                                    >
                                        <Settings size={18} className="text-primary" />
                                    </motion.button>
                                )}
                            </div>
                        )}
                    </AnimatePresence>

                    {/* Sorting UI */}
                    <div className="flex p-1 bg-muted/50 backdrop-blur-xl border border-border rounded-xl w-fit">
                        <button
                            onClick={() => setSortBy('name')}
                            className={cn(
                                "px-4 py-2 rounded-lg text-sm font-medium transition-all",
                                sortBy === 'name'
                                    ? "bg-primary text-primary-foreground shadow-lg"
                                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                            )}
                        >
                            Ordre alphabétique
                        </button>
                        <button
                            onClick={() => setSortBy('loans')}
                            className={cn(
                                "px-4 py-2 rounded-lg text-sm font-medium transition-all",
                                sortBy === 'loans'
                                    ? "bg-primary text-primary-foreground shadow-lg"
                                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                            )}
                        >
                            Nombre d'emprunts
                        </button>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <AnimatePresence mode="popLayout">
                    {filteredMembers
                        .filter((member) => (member.name.toLowerCase().includes(searchQuery.toLowerCase()) || member.email.toLowerCase().includes(searchQuery.toLowerCase())))
                        .sort((a, b) => {
                            if (sortBy === 'loans') {
                                const countA = loanCounts[a.ID || a.id] || 0;
                                const countB = loanCounts[b.ID || b.id] || 0;
                                return countB - countA;
                            }
                            return a.name.localeCompare(b.name);
                        })
                        .map((member) => (
                            <MemberCard
                                key={member.ID || member.id}
                                {...member}
                                loanCount={loanCounts[member.ID || member.id] || 0}
                                onClick={(ev) => { setSelectedMemberId(member.ID || member.id); }}
                                onPrintQR={(m) => setPrintQRMember(m)}
                                delay={0}
                            />
                        ))}
                </AnimatePresence>
            </div>

            <AddMemberModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onAdd={handleAddMember}
                category={activeCategory}
                onRefresh={refreshMembers}
            />

            <MemberDetailsModal
                isOpen={!!selectedMemberId}
                onClose={() => setSelectedMemberId(null)}
                member={members.find(m => (m.ID || m.id) === selectedMemberId)}
                currentUser={currentUser}
                onPrintQR={setPrintQRMember}
            />

            <CleanupMembersModal
                isOpen={isCleanupModalOpen}
                onClose={() => setIsCleanupModalOpen(false)}
                members={oldMembers}
                onRefresh={refreshMembers}
            />

            <PrintQRModal
                isOpen={!!printQRMember}
                onClose={() => setPrintQRMember(null)}
                title={printQRMember?.name}
                id={printQRMember?.ID || printQRMember?.id}
                qrcodeData={printQRMember?.qrcode_data}
                codeEtablissement={currentUser?.code_etablissement}
                role={printQRMember?.role}
                classe={printQRMember?.classe}
            />

            <ExportCSVModal
                isOpen={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
                onExport={handleCSVExport}
            />

            <ImportCSVModal
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
                status={importStatus}
                progress={importProgress}
                summary={importSummary}
                onFinish={refreshMembers}
            />

            <ManageClassModal
                isOpen={isManageClassModalOpen}
                onClose={() => setIsManageClassModalOpen(false)}
                currentClass={selectedClass}
                students={allowedMembers.filter(m => m.role === 'Étudiant' && m.classe === selectedClass)}
                availableClasses={availableClasses}
                currentUser={currentUser}
                onRefresh={refreshMembers}
            />
        </div>
    );
}
